import { useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";
import type React from "react";
import { useEffect } from "react";

/**
 * Sets the Auth0 user id on Sentry's scope so client-side errors and
 * Session Replays are filterable by user.
 *
 * PHI policy: era-web is a patient/consumer-facing app, so unlike the
 * provider-facing repos (era-api, era-bot) we deliberately omit:
 * - `email` — patient email is PHI under HIPAA
 * - `username` / `name` — patient name is PHI
 * - `ip_address` — correlates directly with patient identity; setting
 *   `{{auto}}` would also override `sendDefaultPii: false` in instrument.ts
 *
 * Only `id` (the opaque Auth0 sub) is set. If a future debugging need
 * requires email/name, look up via the Auth0 Management API with audit
 * logging — don't widen this object.
 */
export function SentryUserProvider({
	children,
}: {
	children: React.ReactNode;
}) {
	const { user, isAuthenticated, isLoading } = useAuth0();

	useEffect(() => {
		if (isLoading) return;

		if (isAuthenticated && user?.sub) {
			Sentry.setUser({ id: user.sub });
		} else {
			Sentry.setUser(null);
		}
	}, [isAuthenticated, user, isLoading]);

	return <>{children}</>;
}
